import React from "react";
import { Link } from "react-router-dom";
import { motion, Variants } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FeatureSlideshow from "./FeatureSlideshow";
import {
  Shield,
  Users,
  DollarSign,
  BarChart3,
  MapPin,
  Clock,
  Star,
  ArrowRight,
  Play,
  Zap,
  CheckCircle,
  LucideIcon,
} from "lucide-react";

/* ---------------- TYPES ---------------- */

interface Slide {
  title: string;
  description: string;
}

interface Feature {
  icon: LucideIcon;
  title: string;
  desc: string;
}

interface Testimonial {
  name: string;
  role: string;
  text: string;
}

/* ---------------- ANIMATION ---------------- */

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

/* ---------------- COMPONENT ---------------- */

const LandingPage: React.FC = () => {
  const slides: Slide[] = [
    {
      title: "Unified Retail Platform",
      description:
        "POS billing, online orders, inventory, and delivery tracking in one system.",
    },
    {
      title: "Real-Time Operations",
      description: "Live order status, stock updates, and driver tracking.",
    },
    {
      title: "Fast & Reliable POS",
      description: "High-speed billing with offline support.",
    },
    {
      title: "Cloud or On-Premise",
      description: "Flexible deployment with high availability.",
    },
    {
      title: "Scalable for Growth",
      description: "From single stores to multi-branch retail chains.",
    },
  ];

  const features: Feature[] = [
    {
      icon: Users,
      title: "Customer & Staff Management",
      desc: "Profiles, roles, permissions, and activity tracking.",
    },
    {
      icon: DollarSign,
      title: "Smart Pricing & Offers",
      desc: "Dynamic pricing, discounts, and commission tracking.",
    },
    {
      icon: BarChart3,
      title: "Analytics & Reports",
      desc: "Sales, inventory, and profit insights in real time.",
    },
    {
      icon: MapPin,
      title: "Delivery Tracking",
      desc: "Live driver tracking with optimized routes.",
    },
    {
      icon: Shield,
      title: "Enterprise-Grade Security",
      desc: "Role-based access and encrypted data.",
    },
    {
      icon: Clock,
      title: "24/7 Operations",
      desc: "Always-on system with alerts and monitoring.",
    },
  ];

  const testimonials: Testimonial[] = [
    {
      name: "Retail Chain Owner",
      role: "Multi-Store Grocery",
      text:
        "RetailNextBox unified our POS, online orders, and delivery in one dashboard.",
    },
    {
      name: "Store Manager",
      role: "Supermarket",
      text:
        "Billing is fast, inventory is accurate, and reports are instant.",
    },
    {
      name: "Founder",
      role: "Retail Startup",
      text:
        "We launched faster and scaled without tech headaches.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted/30">
      {/* NAVBAR */}
      <nav className="sticky top-0 z-50 border-b bg-background/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-1 flex h-16 items-center justify-between">
          <img
            src="/assets/logo/rnb-logo.png"
            alt="RetailNextBox"
            className="h-60 w-80"
          />
          <div className="flex gap-3">
            <Link to="/login">
              <Button className="bg-gradient-primary">Demo Login</Button>
            </Link>
            <Link to="/requestdemo">
              <Button className="bg-gradient-primary">Request Demo</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <Badge className="bg-primary/10 text-primary inline-flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Next-Gen Retail Platform
            </Badge>

            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              Run Your Retail Business{" "}
              <span className="text-transparent bg-clip-text bg-gradient-primary">
                Smarter
              </span>
            </h1>

            <p className="text-lg text-muted-foreground">
              RetailNextBox is an all-in-one POS billing, online grocery ordering,
              inventory, and delivery tracking platform built for modern
              retailers.
            </p>

            {/* OFFLINE BADGE */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full 
                         bg-green-100 text-green-700 text-xs font-medium w-fit"
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600" />
              </span>
              Works Offline (POS Safe)
            </motion.div>

            <div className="flex gap-4 pt-2">
              <Link to="/pricing">
                <Button size="lg" className="bg-gradient-primary">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  View Pricing
                </Button>
              </Link>
              <Link to="/requestdemo">
                <Button size="lg" variant="outline">
                  <Play className="w-4 h-4 mr-2" />
                  Request Demo
                </Button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-6 pt-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" />
                POS Billing
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" />
                Online Orders
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-primary" />
                Delivery Tracking
              </div>
            </div>
          </motion.div>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ delay: 0.2 }}
          >
            <FeatureSlideshow slides={slides} />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <Badge className="bg-primary/10 text-primary">Features</Badge>
            <h2 className="text-3xl font-bold mt-3">
              Everything You Need to Scale Retail
            </h2>
            <p className="text-muted-foreground mt-2">
              Built for speed, reliability, and growth
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -6 }}
              >
                <Card className="h-full">
                  <CardContent className="p-6 space-y-3">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      <f.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-semibold text-lg">{f.title}</h3>
                    <p className="text-muted-foreground text-sm">{f.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-muted/40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <Badge className="bg-primary/10 text-primary">Testimonials</Badge>
            <h2 className="text-3xl font-bold mt-3">Loved by Retailers</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex">
                    {[...Array(5)].map((_, j) => (
                      <Star
                        key={j}
                        className="w-4 h-4 text-yellow-400 fill-yellow-400"
                      />
                    ))}
                  </div>
                  <p className="text-muted-foreground">“{t.text}”</p>
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-sm text-muted-foreground">{t.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-primary text-primary-foreground text-center">
        <h2 className="text-3xl font-bold">
          Ready to Modernize Your Retail?
        </h2>
        <p className="mt-3 opacity-90">
          POS • Online Orders • Inventory • Delivery
        </p>
        <div className="mt-6 flex justify-center gap-4">
          <Link to="/pricing">
            <Button size="lg" className="bg-background text-foreground">
              View Pricing Plans
            </Button>
          </Link>
          <Link to="/requestdemo">
            <Button
              size="lg"
              variant="outline"
              className="border-white text-white"
            >
              Talk to Sales
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t py-8 text-center text-sm text-muted-foreground">
        © 2025 RetailNextBox. Built for the future of retail.
      </footer>
    </div>
  );
};

export default LandingPage;
